import React, { useEffect, useMemo, useState } from "react";
import { api } from "../Assets/assetsShared";
import { Badges, fmtDate } from "./ReviewParts";
import "./EmployeeReview.css";

// ready made review points (any other point can be typed in the form)
const POINTS = [
  "Punctuality",
  "Quality of Work",
  "Teamwork",
  "Communication",
  "Time Management",
  "Problem Solving",
  "Learning Attitude",
  "Responsibility",
  "Client Handling",
  "Initiative",
  "Documentation",
  "Following Standards",
];

const EMPTY = { employee: "", suggestion: "" };

/* Chips to pick the review points of one kind (positive or improvement) */
function PointPicker({ title, hint, kind, selected, onChange }) {
  const [custom, setCustom] = useState("");
  const [extra, setExtra] = useState([]);

  const options = Array.from(new Set([...POINTS, ...extra]));

  const toggle = (name) =>
    onChange(
      selected.includes(name)
        ? selected.filter((n) => n !== name)
        : [...selected, name],
    );

  const addCustom = () => {
    const name = custom.trim();
    if (!name) return;

    const existing = options.find(
      (o) => o.toLowerCase() === name.toLowerCase(),
    );
    const finalName = existing || name;

    if (!existing) setExtra((prev) => [...prev, name]);
    if (!selected.includes(finalName)) onChange([...selected, finalName]);

    setCustom("");
  };

  return (
    <div className={`er-picker er-picker-${kind}`}>
      <div className="er-picker-head">
        <h4>{title}</h4>
        <span>{hint}</span>
      </div>

      <div className="er-chips">
        {options.map((name) => (
          <button
            type="button"
            key={name}
            className={`er-chip ${selected.includes(name) ? "on" : ""}`}
            onClick={() => toggle(name)}
          >
            {selected.includes(name) ? "✓ " : ""}
            {name}
          </button>
        ))}
      </div>

      <div className="er-custom">
        <input
          placeholder="Add another point..."
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <button
          type="button"
          className="er-btn er-btn-light"
          onClick={addCustom}
        >
          Add
        </button>
      </div>
    </div>
  );
}

// Goals counters of one employee (Goals = Achieved + Pending)
function GoalBadges({ goals }) {
  const achieved = goals.filter((g) => g.status === "achieved").length;

  return (
    <span className="er-badges">
      <span className="er-badge er-b-pos">Goals : {goals.length}</span>
      <span className="er-badge er-b-ach">Achieved : {achieved}</span>
      <span className="er-badge er-b-pen">
        Pending : {goals.length - achieved}
      </span>
    </span>
  );
}

export default function GiveReview() {
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [positive, setPositive] = useState([]);
  const [improvement, setImprovement] = useState([]);
  const [reviewSearch, setReviewSearch] = useState("");
  const [goalSearch, setGoalSearch] = useState("");
  const [openReviewId, setOpenReviewId] = useState(null);
  const [openGoalId, setOpenGoalId] = useState(null);
  const [replies, setReplies] = useState({}); // goalId -> reply text being typed
  const [editingReply, setEditingReply] = useState(null); // goalId being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      const [empRes, listRes] = await Promise.all([
        api.get("/employee-reviews/employees"),
        api.get("/employee-reviews"),
      ]);

      setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setRows(Array.isArray(listRes.data) ? listRes.data : []);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
      else
        setMessage(
          err.response?.data?.message || "Could not load the reviews.",
        );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (saving) return;

    if (!form.employee) {
      setMessage("Select an employee.");
      return;
    }
    if (!positive.length && !improvement.length && !form.suggestion.trim()) {
      setMessage("Select at least one review point or write a suggestion.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/employee-reviews", {
        employee: form.employee,
        points: [
          ...positive.map((name) => ({ name, type: "positive" })),
          ...improvement.map((name) => ({ name, type: "improvement" })),
        ],
        suggestion: form.suggestion,
      });

      setMessage("Review submitted. The employee can see it now.");
      setOpenReviewId(form.employee);
      setForm(EMPTY);
      setPositive([]);
      setImprovement([]);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not submit the review.");
    } finally {
      setSaving(false);
    }
  };

  // only admin / HR can mark an improvement point as achieved
  const setPoint = async (reviewId, pointId, status) => {
    try {
      await api.patch(`/employee-reviews/${reviewId}/points/${pointId}`, {
        status,
      });
      setMessage(
        status === "achieved"
          ? "The point is marked as achieved."
          : "The point is back to pending.",
      );
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not update the point.");
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;

    try {
      await api.delete(`/employee-reviews/${id}`);
      setMessage("Review deleted.");
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not delete the review.");
    }
  };

  /* ---------- reply to a goal ---------- */
  const startEditReply = (goal) => {
    setReplies((prev) => ({ ...prev, [goal._id]: goal.adminReply || "" }));
    setEditingReply(goal._id);
  };

  const cancelEditReply = (goalId) => {
    setEditingReply(null);
    setReplies((prev) => {
      const next = { ...prev };
      delete next[goalId];
      return next;
    });
  };

  const saveReply = async (goal) => {
    const text = (replies[goal._id] ?? goal.adminReply ?? "").trim();

    if (!text && !goal.adminReply) {
      setMessage("Write your reply first.");
      return;
    }

    try {
      await api.patch(`/employee-reviews/goals/${goal._id}/reply`, {
        reply: text,
      });

      setMessage(
        text
          ? "Reply saved. The employee can see it in My Goals."
          : "Reply removed.",
      );

      cancelEditReply(goal._id);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not save the reply.");
    }
  };

  /* ---------- reviews and goals are listed separately ---------- */
  const reviewRows = useMemo(() => {
    const q = reviewSearch.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.reviews.length > 0 &&
        (!q || r.employee.name.toLowerCase().includes(q)),
    );
  }, [rows, reviewSearch]);

  const goalRows = useMemo(() => {
    const q = goalSearch.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.goals.length > 0 && (!q || r.employee.name.toLowerCase().includes(q)),
    );
  }, [rows, goalSearch]);

  const reviewTotals = rows.reduce(
    (t, r) => ({
      employees: t.employees + (r.reviews.length > 0 ? 1 : 0),
      positive: t.positive + r.counts.positive,
      improvement: t.improvement + r.counts.improvement,
      pending: t.pending + r.counts.pending,
    }),
    { employees: 0, positive: 0, improvement: 0, pending: 0 },
  );

  const goalTotal = rows.reduce((sum, r) => sum + r.goals.length, 0);

  if (forbidden) {
    return (
      <div className="er-page">
        <header className="er-banner">
          <div>
            <h1>Create Employee Review</h1>
            <p>Give reviews to employees.</p>
          </div>
        </header>

        <div className="er-message">
          Your role does not have the "Create" permission for Employee Review.
          Ask the admin to tick "Create" under Employee Review in Role &amp;
          Permission for your role, then refresh this page.
        </div>
      </div>
    );
  }

  return (
    <div className="er-page">
      {/* ----- banner ----- */}
      <header className="er-banner">
        <div>
          <h1>Create Employee Review</h1>
          <p>
            Tell the employee what they do well and what they need to improve.
          </p>
        </div>

        <div className="er-stats">
          <div className="er-stat">
            <strong>{reviewTotals.employees}</strong>
            <span>Reviewed</span>
          </div>
          <div className="er-stat">
            <strong>{reviewTotals.positive}</strong>
            <span>Positive</span>
          </div>
          <div className="er-stat">
            <strong>{reviewTotals.improvement}</strong>
            <span>Improvement</span>
          </div>
          <div className="er-stat">
            <strong>{reviewTotals.pending}</strong>
            <span>Pending</span>
          </div>
          <div className="er-stat">
            <strong>{goalTotal}</strong>
            <span>Goals</span>
          </div>
        </div>
      </header>

      {message && <div className="er-message">{message}</div>}

      {/* ----- review form ----- */}
      <section className="er-card">
        <h3>Employee Review Form</h3>

        <label className="er-field">
          <span>Employee</span>
          <select
            value={form.employee}
            onChange={(e) =>
              setForm((f) => ({ ...f, employee: e.target.value }))
            }
          >
            <option value="">Select employee</option>
            {employees.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>

        <PointPicker
          kind="positive"
          title="Good at"
          hint="Select the points where the employee is doing well"
          selected={positive}
          onChange={setPositive}
        />

        <PointPicker
          kind="improvement"
          title="Needs improvement"
          hint="Select the points the employee should improve"
          selected={improvement}
          onChange={setImprovement}
        />

        <label className="er-field">
          <span>Suggestion from Management</span>
          <textarea
            placeholder="Write your suggestion for the employee..."
            value={form.suggestion}
            onChange={(e) =>
              setForm((f) => ({ ...f, suggestion: e.target.value }))
            }
          />
        </label>

        <button className="er-btn" onClick={submit} disabled={saving}>
          {saving ? "Submitting..." : "Submit Review"}
        </button>
      </section>

      {/* ================= EMPLOYEE WISE REVIEWS ================= */}
      <section className="er-card">
        <div className="er-list-head">
          <h3>Employee Wise Reviews</h3>
          <input
            className="er-search"
            type="search"
            placeholder="Search employee..."
            value={reviewSearch}
            onChange={(e) => setReviewSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="er-empty">Loading...</div>
        ) : reviewRows.length === 0 ? (
          <div className="er-empty">No reviews yet.</div>
        ) : (
          reviewRows.map((row) => {
            const id = String(row.employee._id);
            const open = openReviewId === id;

            return (
              <div className="er-emp" key={id}>
                <button
                  type="button"
                  className="er-emp-head"
                  onClick={() => setOpenReviewId(open ? null : id)}
                >
                  <strong>{row.employee.name}</strong>
                  <Badges counts={row.counts} />
                  <span className={`er-arrow ${open ? "open" : ""}`}>▾</span>
                </button>

                {open && (
                  <div className="er-emp-body">
                    {row.reviews.map((r) => {
                      const good = r.points.filter(
                        (p) => p.type === "positive",
                      );
                      const improve = r.points.filter(
                        (p) => p.type === "improvement",
                      );

                      return (
                        <article className="er-review" key={r._id}>
                          <div className="er-review-head">
                            <span className="er-muted">
                              {fmtDate(r.createdAt)} · by {r.createdByName}
                            </span>
                            <button
                              className="er-del"
                              onClick={() => removeReview(r._id)}
                            >
                              Delete
                            </button>
                          </div>

                          {(good.length > 0 || improve.length > 0) && (
                            <div className="er-two">
                              {good.length > 0 && (
                                <div className="er-panel er-panel-pos">
                                  <h4 className="er-h-pos">Good at</h4>
                                  <div className="er-points">
                                    {good.map((p) => (
                                      <span
                                        key={p._id}
                                        className="er-point er-point-positive"
                                      >
                                        <b>{p.name}</b>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {improve.length > 0 && (
                                <div className="er-panel er-panel-imp">
                                  <h4 className="er-h-imp">
                                    Needs improvement
                                  </h4>

                                  {improve.map((p) => (
                                    <div className="er-imp-row" key={p._id}>
                                      <span className="er-point er-point-improvement">
                                        <b>{p.name}</b>
                                      </span>

                                      <em className={`er-status ${p.status}`}>
                                        {p.status === "achieved"
                                          ? `Achieved${
                                              p.achievedAt
                                                ? " on " + fmtDate(p.achievedAt)
                                                : ""
                                            }`
                                          : "Pending"}
                                      </em>

                                      <button
                                        className={`er-btn er-btn-small ${
                                          p.status === "achieved"
                                            ? "er-btn-light"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          setPoint(
                                            r._id,
                                            p._id,
                                            p.status === "achieved"
                                              ? "pending"
                                              : "achieved",
                                          )
                                        }
                                      >
                                        {p.status === "achieved"
                                          ? "Undo"
                                          : "Mark as achieved"}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {r.suggestion && (
                            <div className="er-bubble er-bubble-mgmt">
                              <div className="er-bubble-head">
                                <b>Suggestion</b>
                              </div>
                              <p>{r.suggestion}</p>
                            </div>
                          )}

                          {r.employeeNote && (
                            <div className="er-bubble er-bubble-me">
                              <div className="er-bubble-head">
                                <b>Employee update</b>
                                {r.employeeNoteAt && (
                                  <span className="er-muted">
                                    {fmtDate(r.employeeNoteAt)}
                                  </span>
                                )}
                              </div>
                              <p>{r.employeeNote}</p>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>

      {/* ================= EMPLOYEE WISE GOALS ================= */}
      <section className="er-card">
        <div className="er-list-head">
          <h3>Employee Wise Goals</h3>
          <input
            className="er-search"
            type="search"
            placeholder="Search employee..."
            value={goalSearch}
            onChange={(e) => setGoalSearch(e.target.value)}
          />
        </div>

        <p className="er-hint">
          Only the goals that the employees shared with admin are shown here.
          Your reply is shown to the employee in My Goals.
        </p>

        {loading ? (
          <div className="er-empty">Loading...</div>
        ) : goalRows.length === 0 ? (
          <div className="er-empty">No goals shared yet.</div>
        ) : (
          goalRows.map((row) => {
            const id = String(row.employee._id);
            const open = openGoalId === id;

            return (
              <div className="er-emp" key={id}>
                <button
                  type="button"
                  className="er-emp-head"
                  onClick={() => setOpenGoalId(open ? null : id)}
                >
                  <strong>{row.employee.name}</strong>
                  <GoalBadges goals={row.goals} />
                  <span className={`er-arrow ${open ? "open" : ""}`}>▾</span>
                </button>

                {open && (
                  <div className="er-emp-body">
                    {row.goals.map((g) => {
                      const editing = editingReply === g._id;

                      return (
                        <article className="er-goal" key={g._id}>
                          <div className="er-goal-top">
                            <div className="er-goal-main">
                              <strong>{g.title}</strong>
                              {g.description && (
                                <p className="er-text">{g.description}</p>
                              )}
                              <span className="er-muted">
                                {g.targetDate
                                  ? `Target: ${g.targetDate}`
                                  : "No target date"}
                              </span>
                            </div>

                            <em className={`er-status ${g.status}`}>
                              {g.status === "achieved"
                                ? `Achieved${
                                    g.achievedAt
                                      ? " on " + fmtDate(g.achievedAt)
                                      : ""
                                  }`
                                : "Pending"}
                            </em>
                          </div>

                          {/* ----- reply: bubble after sending, box only to write ----- */}
                          {g.adminReply && !editing ? (
                            <div className="er-bubble er-bubble-me">
                              <div className="er-bubble-head">
                                <b>Your reply</b>
                                <span className="er-muted">
                                  {g.repliedByName
                                    ? `${g.repliedByName} · `
                                    : ""}
                                  {fmtDate(g.repliedAt)}
                                </span>
                                <button
                                  type="button"
                                  className="er-link-btn"
                                  onClick={() => startEditReply(g)}
                                >
                                  Edit
                                </button>
                              </div>
                              <p>{g.adminReply}</p>
                            </div>
                          ) : (
                            <div className="er-composer">
                              <label>Reply to this goal</label>

                              <textarea
                                placeholder="Write a reply to this goal..."
                                value={replies[g._id] ?? ""}
                                onChange={(e) =>
                                  setReplies((prev) => ({
                                    ...prev,
                                    [g._id]: e.target.value,
                                  }))
                                }
                              />

                              <div className="er-composer-actions">
                                {editing && (
                                  <button
                                    type="button"
                                    className="er-btn er-btn-small er-btn-light"
                                    onClick={() => cancelEditReply(g._id)}
                                  >
                                    Cancel
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="er-btn er-btn-small"
                                  onClick={() => saveReply(g)}
                                >
                                  {g.adminReply ? "Update Reply" : "Send Reply"}
                                </button>
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
